import { useEffect, useState, useRef } from "react";

class Cloud {
  constructor(pos, r, sf, angle) {
    this.pos = pos;
    this.r = r;
    this.amp = this.r * 1.2;
    this.sf = sf;
    this.angle = angle;
  }
  
    drawBg(ctx, fc, pt) {
        const angle = Math.atan2(this.pos.y, this.pos.x) * 180 / Math.PI;
        const bgMotion = fc * 0.8;
        ctx.save();
        ctx.translate(this.pos.x, this.pos.y);
        ctx.rotate((90 + angle) * Math.PI / 180);
        ctx.scale(this.sf, this.sf);

        if (typeof pt === 'string') {
            ctx.fillStyle = pt;
        } else {
            ctx.fillStyle = `rgb(${pt}, ${pt}, ${pt})`;
        }
        
        ctx.beginPath();
        ctx.ellipse(0, 0, (this.r + bgMotion) / 2, (this.r * 1.2 + bgMotion) / 2, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
    }
  
  drawFg(ctx, fc, bg) {
    const angle = Math.atan2(this.pos.y, this.pos.x) * 180 / Math.PI;
    const motion = Math.abs(this.r * 0.75 + Math.sin(fc * 0.5) * this.r * 2);
    
    ctx.save();
    ctx.translate(this.pos.x * 0.5, this.pos.y * 0.5);
    ctx.rotate((90 + angle) * Math.PI / 180);
    ctx.scale(this.sf, this.sf);
    
    ctx.fillStyle = `rgb(${bg}, ${bg}, ${bg})`;
    ctx.beginPath();
    ctx.ellipse(0, 0, motion / 2, (motion * 1.2) / 2, 0, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.restore();
  }
  
  drawFgClear(ctx, fc) {
    const angle = Math.atan2(this.pos.y, this.pos.x) * 180 / Math.PI;
    const motion = Math.abs(this.r * 0.75 + Math.sin(fc * 0.5) * this.r * 2);
    
    ctx.save();
    ctx.translate(this.pos.x * 0.5, this.pos.y * 0.5);
    ctx.rotate((90 + angle) * Math.PI / 180);
    ctx.scale(this.sf, this.sf);
    
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.ellipse(0, 0, motion / 2, (motion * 1.2) / 2, 0, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.restore();
  }
}

export default function LogoPlayer(){
    const [logos, setLogos] = useState([]);
    const [index, setIndex] = useState(0);
    const [animating, setAnimating] = useState(false);
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    const timeoutRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const res = await fetch("/assets/logos/logos.json", { cache: "no-cache" });
                if (!res.ok) throw new Error("manifest not found");
                const data = await res.json();
                const names = Array.isArray(data) ? data : (Array.isArray(data.files) ? data.files : []);
                const urls = names.map((name) => `/assets/logos/${name}`);
                if (!cancelled) setLogos(urls);
            } catch (e) {
                const candidates = [];
                const exts = ['.png', '.gif', '.jpg', '.jpeg', '.svg'];
                for (let i = 1; i <= 50; i++) {
                    exts.forEach(ext => candidates.push(`/assets/logos/logo${i}${ext}`));
                }
                const checks = candidates.map((src) => new Promise((resolve) => {
                    const img = new Image();
                    img.onload = () => resolve(src);
                    img.onerror = () => resolve(null);
                    img.src = src;
                }));
                Promise.all(checks).then((results) => {
                    const urls = results.filter(Boolean);
                    if (!cancelled) setLogos(urls);
                });
            }
        };
        load();
        return () => { cancelled = true; };
    }, []);

    const runAnimation = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        
        const ctx = canvas.getContext('2d');
        const w = 360; // Match image width
        const h = 360;
        canvas.width = w;
        canvas.height = h;
        
        const pt = '#eee';
        const no = 6;
        const angleOffset = 360 / no;
        
        const clouds = [];
        for (let i = 0; i < no; i++) {
            const r = 360; // Increased from 200 to match image size
            const angle = angleOffset * i;
            const x = (Math.cos(angle * Math.PI / 180) * r) / 6;
            const y = (Math.sin(angle * Math.PI / 180) * r) / 6;
            const pos = { x, y };
            const sf = Math.random() * 0.25 + 0.25;
            clouds.push(new Cloud(pos, r, sf, angle));
        }
        
        let fc = 0;
        const fps = 60;
        const frameDuration = 1000 / fps;
        let lastTime = performance.now();
        
        const animate = (currentTime) => {
            const elapsed = currentTime - lastTime;
            
            if (elapsed >= frameDuration) {
                ctx.clearRect(0, 0, w, h); // CLEAR
                // ctx.fillStyle = 'black';
                // ctx.fillRect(0, 0, w, h);
                
                ctx.save();
                ctx.translate(w / 2, h / 2);
                
                for (let i = 0; i < clouds.length; i++) {
                    clouds[i].drawBg(ctx, fc * 0.05, pt);
                    clouds[i].drawFgClear(ctx, fc * 0.05);
                }
                
                ctx.restore();
                
                fc++;
                lastTime = currentTime;
            }
            
            animationRef.current = requestAnimationFrame(animate);
        };
        
        animationRef.current = requestAnimationFrame(animate);
        
        // Stop after 2 seconds
        timeoutRef.current = setTimeout(() => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            setAnimating(false);
        }, 2000);
    };

    const next = () => {
        if (!hasLogos) return;
        
        // Clear any existing animation and timeout
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        
        setAnimating(true);
        setIndex((i) => (i + 1) % logos.length);
        
        setTimeout(() => {
            runAnimation();
        }, 0);
    };

    useEffect(() => {
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const hasLogos = logos.length > 0;
    const currentSrc = hasLogos ? logos[index % logos.length] : null;

    return(
        <div className="page" style={{ display: "grid", gap: 16, background : "white" }}>
            {!hasLogos && (
                <div style={{ color: "#888" }}>
                    No logos found. Add images to public/assets/logos.
                </div>
            )}
            <div style={{ position: "relative", width: 400, height: 400, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {hasLogos && (
                    <img
                        src={currentSrc}
                        alt={`Logo ${index + 1} of ${logos.length}`}
                        style={{ position: "absolute", top: 0, left: 0, width: 400, height: 400, objectFit: "contain", zIndex: 1 }}
                    />
                )}
                {animating && (
                    <canvas ref={canvasRef}
                        width={400}
                        height={400}
                        style={{ position: "absolute", top: 0, left: 0, width: 400, height: 400, zIndex: 2, pointerEvents: "none" }}
                    />
                )}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                <button onClick={next} disabled={!hasLogos}>Next</button>
                <p>{ index + 1 } / { logos.length }</p>
            </div>
        </div>
    )
}